import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  type OverstayChargeMode,
  type OverstayPolicyDto,
  deleteOverstayPolicy,
  getOverstayPolicy,
  putOverstayPolicy,
} from '@/lib/api';
import { formatPriceMinor } from '@/lib/format';
import { AlertTriangle, Moon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

// ---------------------------------------------------------------------------
// Migration 0103 — boarding overstay policy. The boarding sibling of
// `/settings/daycare/late-pickup`: a stay still checked in past its scheduled
// checkout day + cutoff hour + grace owes an extra night, charged as the
// nightly rate, a flat fee, or both. The hourly cron adds the nights; this
// page owns the policy. Money on the wire is minor units; the fee input is
// major units for the human.
// ---------------------------------------------------------------------------

const CURRENCY_SYMBOLS: Record<string, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  CAD: '$',
  AUD: '$',
  NZD: '$',
};

function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? currency;
}

function minorToMajor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function majorToMinor(major: string): number | null {
  const trimmed = major.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return null;
  return Math.round(parsed * 100);
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

const DEFAULT_POLICY: OverstayPolicyDto = {
  enabled: false,
  checkoutCutoffHour: 11,
  graceMinutes: 0,
  chargeMode: 'nightly_rate',
  feeMinor: 0,
  autoCharge: false,
  notifyCustomer: true,
};

const CHARGE_MODES: ReadonlyArray<{ value: OverstayChargeMode; title: string; detail: string }> = [
  {
    value: 'nightly_rate',
    title: 'Nightly rate',
    detail:
      'Each extra night is priced through your normal boarding rates (seasonal and day-of-week rules included).',
  },
  {
    value: 'flat_fee',
    title: 'Flat fee per night',
    detail: 'A fixed amount per extra night instead of the nightly rate.',
  },
  {
    value: 'nightly_rate_plus_fee',
    title: 'Nightly rate plus a fee',
    detail: 'The normal nightly rate with a fixed late-collection fee on top.',
  },
];

const INPUT_CLASS =
  'w-full rounded-md border border-border bg-surface px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/40';
const LABEL_CLASS =
  'mb-1.5 block text-[10.5px] font-medium uppercase tracking-[0.14em] text-muted-foreground';

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

function OverstayPolicyForm({
  initial,
  configured,
  currency,
  onSaved,
  onCleared,
}: {
  initial: OverstayPolicyDto;
  configured: boolean;
  currency: string;
  onSaved: () => void;
  onCleared: () => void;
}) {
  const [enabled, setEnabled] = useState<boolean>(initial.enabled);
  const [cutoffHour, setCutoffHour] = useState<string>(String(initial.checkoutCutoffHour));
  const [graceMinutes, setGraceMinutes] = useState<string>(String(initial.graceMinutes));
  const [chargeMode, setChargeMode] = useState<OverstayChargeMode>(initial.chargeMode);
  const [feeMajor, setFeeMajor] = useState<string>(minorToMajor(initial.feeMinor));
  const [autoCharge, setAutoCharge] = useState<boolean>(initial.autoCharge);
  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(initial.notifyCustomer);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const feeApplies = chargeMode !== 'nightly_rate';

  const preview = useMemo(() => {
    const cutoff = Number(cutoffHour);
    const grace = Number(graceMinutes);
    const feeMinor = majorToMinor(feeMajor) ?? 0;
    if (!Number.isInteger(cutoff) || cutoff < 0 || cutoff > 23) return null;
    if (!Number.isInteger(grace) || grace < 0) return null;
    const when = `${pad2(cutoff)}:00${grace > 0 ? ` (plus ${grace} minutes grace)` : ''}`;
    const fee = formatPriceMinor(feeMinor, currency);
    const charge =
      chargeMode === 'nightly_rate'
        ? 'the nightly rate'
        : chargeMode === 'flat_fee'
          ? `a ${fee} fee`
          : `the nightly rate plus a ${fee} fee`;
    return `A pet still here after ${when} on its checkout day is charged ${charge} for each extra night.`;
  }, [cutoffHour, graceMinutes, feeMajor, chargeMode, currency]);

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    const cutoff = Number(cutoffHour);
    const grace = Number(graceMinutes);
    const feeMinor = feeApplies ? majorToMinor(feeMajor) : 0;

    if (!Number.isInteger(cutoff) || cutoff < 0 || cutoff > 23) {
      setError('Cutoff hour must be an integer between 0 and 23.');
      return;
    }
    if (!Number.isInteger(grace) || grace < 0 || grace > 720) {
      setError('Grace minutes must be an integer between 0 and 720.');
      return;
    }
    if (feeMinor === null) {
      setError('Fee must be a non-negative amount.');
      return;
    }
    if (feeApplies && feeMinor === 0) {
      setError('Enter a fee greater than zero for fee-based charge modes.');
      return;
    }

    setSaving(true);
    try {
      await putOverstayPolicy({
        enabled,
        checkoutCutoffHour: cutoff,
        graceMinutes: grace,
        chargeMode,
        feeMinor,
        autoCharge,
        notifyCustomer,
      });
      setSuccess('Saved.');
      onSaved();
    } catch (err) {
      setError(
        err instanceof ApiError && err.body?.issues
          ? (err.body.issues as string[]).join('; ')
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleClear() {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteOverstayPolicy();
      setEnabled(false);
      setAutoCharge(false);
      onCleared();
    } catch {
      setError('Could not clear the policy. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSave(e)} className="space-y-5">
      {error && (
        <p className="text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="text-sm text-emerald-700" role="status">
          {success}
        </p>
      )}

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
          disabled={saving}
          className="size-4"
          data-testid="overstay-enabled"
        />
        <span>Charge for boarding overstays</span>
      </label>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="overstay-cutoff-hour" className={LABEL_CLASS}>
            Checkout cutoff hour (facility-local)
          </label>
          <input
            id="overstay-cutoff-hour"
            type="number"
            min={0}
            max={23}
            value={cutoffHour}
            onChange={(e) => setCutoffHour(e.target.value)}
            disabled={saving}
            className={INPUT_CLASS}
            data-testid="overstay-cutoff-hour"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Hour (0-23) on the checkout day after which the stay counts as an extra night.
          </p>
        </div>
        <div>
          <label htmlFor="overstay-grace-minutes" className={LABEL_CLASS}>
            Grace (minutes)
          </label>
          <input
            id="overstay-grace-minutes"
            type="number"
            min={0}
            max={720}
            value={graceMinutes}
            onChange={(e) => setGraceMinutes(e.target.value)}
            disabled={saving}
            className={INPUT_CLASS}
            data-testid="overstay-grace-minutes"
          />
          <p className="mt-1 text-xs text-muted-foreground">Slack after the cutoff, up to 720.</p>
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className={LABEL_CLASS}>How extra nights are charged</legend>
        {CHARGE_MODES.map((opt) => (
          <label
            key={opt.value}
            className="flex cursor-pointer items-start gap-3 rounded-md border border-border p-3 hover:bg-secondary/40"
          >
            <input
              type="radio"
              name="overstay-charge-mode"
              value={opt.value}
              checked={chargeMode === opt.value}
              onChange={() => setChargeMode(opt.value)}
              disabled={saving}
              className="mt-0.5 size-4"
              data-testid={`overstay-mode-${opt.value}`}
            />
            <div>
              <p className="text-sm font-medium">{opt.title}</p>
              <p className="text-xs text-muted-foreground">{opt.detail}</p>
            </div>
          </label>
        ))}
      </fieldset>

      <div className="max-w-xs">
        <label htmlFor="overstay-fee" className={LABEL_CLASS}>
          Fee per extra night ({currencySymbol(currency)})
        </label>
        <input
          id="overstay-fee"
          type="text"
          inputMode="decimal"
          value={feeMajor}
          onChange={(e) => setFeeMajor(e.target.value)}
          disabled={saving || !feeApplies}
          className={`${INPUT_CLASS} num disabled:opacity-50`}
          data-testid="overstay-fee"
        />
        {!feeApplies && (
          <p className="mt-1 text-xs text-muted-foreground">
            Not used when charging the nightly rate only.
          </p>
        )}
      </div>

      <div className="space-y-3">
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={autoCharge}
            onChange={(e) => setAutoCharge(e.target.checked)}
            disabled={saving}
            className="mt-0.5 size-4"
            data-testid="overstay-auto-charge"
          />
          <span>
            <span className="block">Charge the saved card automatically</span>
            <span className="block text-xs text-muted-foreground">
              Each extra night is charged to the customer's card on file as it is added.
            </span>
          </span>
        </label>
        {autoCharge && (
          <div
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900"
            role="note"
            data-testid="overstay-auto-charge-warning"
          >
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" strokeWidth={1.75} aria-hidden />
            <span>
              This charges the customer off-session, without them present, whether or not the
              service normally bills its balance at checkout. Make sure your booking terms say so.
              Customers with no saved card are flagged to owners and managers instead.
            </span>
          </div>
        )}

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            checked={notifyCustomer}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
            disabled={saving}
            className="mt-0.5 size-4"
            data-testid="overstay-notify-customer"
          />
          <span>
            <span className="block">Tell the customer each time a night is added</span>
            <span className="block text-xs text-muted-foreground">
              Sends the "Extra night added" email and push notification.
            </span>
          </span>
        </label>
      </div>

      {preview && (
        <div
          className="rounded-md border border-border bg-secondary/40 p-3 text-sm text-foreground"
          data-testid="overstay-preview"
        >
          {preview}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 pt-2">
        <button
          type="button"
          onClick={() => void handleClear()}
          disabled={saving || !configured}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-muted-foreground press disabled:opacity-50"
          data-testid="overstay-clear"
        >
          Clear policy
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground press"
          data-testid="overstay-save"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function BoardingOverstays() {
  const { state } = useAuthContext();
  const fallbackCurrency = state.me?.activeAccount?.currency ?? 'GBP';
  const [policy, setPolicy] = useState<OverstayPolicyDto>(DEFAULT_POLICY);
  const [configured, setConfigured] = useState(false);
  const [currency, setCurrency] = useState<string>(fallbackCurrency);
  const [loading, setLoading] = useState(true);
  const [permissionDenied, setPermissionDenied] = useState(false);
  // Remount the form on every successful load so its local state tracks the
  // server row (the same pattern the daycare late-pickup page relies on).
  const [formKey, setFormKey] = useState(0);

  const refresh = useCallback(async () => {
    setLoading(true);
    setPermissionDenied(false);
    try {
      const res = await getOverstayPolicy();
      setPolicy(res.policy);
      setConfigured(res.configured);
      setCurrency(res.currency || fallbackCurrency);
      setFormKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
    } finally {
      setLoading(false);
    }
  }, [fallbackCurrency]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to manage boarding settings
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Ask an owner or manager to grant access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="flex items-center gap-2 font-heading text-2xl font-medium tracking-tight">
          <Moon className="size-5 text-muted-foreground" strokeWidth={1.75} aria-hidden />
          Boarding overstays
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          When a pet is still boarding past its scheduled checkout, add and charge the extra nights
          automatically. Checked every hour in each facility's local time.
        </p>
      </header>

      {loading ? (
        <div className="space-y-3">
          <div className="h-8 w-48 animate-pulse rounded bg-secondary" />
          <div className="h-32 w-full animate-pulse rounded bg-secondary" />
        </div>
      ) : (
        <section className="rounded-2xl bg-surface p-6 ring-1 ring-border elevate">
          <h2 className="mb-4 font-heading text-lg font-medium">Overstay policy</h2>
          <OverstayPolicyForm
            key={formKey}
            initial={policy}
            configured={configured}
            currency={currency}
            onSaved={() => void refresh()}
            onCleared={() => {
              setPolicy(DEFAULT_POLICY);
              setConfigured(false);
              setFormKey((k) => k + 1);
            }}
          />
        </section>
      )}
    </div>
  );
}
